import { Box, HStack, Text } from "@chakra-ui/layout";
import { Button } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type UserProfitReportQuery,
  UtilizationReportDataSource,
  UtilizationReportOrderByColumn,
} from "@src/__generated__/graphql";
import type { UserProfitReportStore } from "@src/components/modules/reports/user-profit-report/store";
import {
  AvailableCapacityTooltip,
  TrackedAndAllocatedTooltip,
  userCell,
} from "@src/components/modules/reports/utilization/UtilizationReport";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  DataTable,
  LoadingOverlay,
} from "@src/components/ui-kit";
import { RangeProgressBar } from "@src/components/ui-kit/ProgressBar/RangeProgressBar";
import { currency } from "@src/utils/formatters";
import { formatSecondsToHours } from "@src/utils/time";
import { differenceInMonths } from "date-fns";
import { observer } from "mobx-react-lite";
import { now } from "mobx-utils";
import { useMemo } from "react";

export type UserProfitReportRow = {
  id: string;
} & UserProfitReportQuery["utilizationReport"]["data"][0];

type UserProfitReportTableProps = {
  store: UserProfitReportStore;
};

export const UserProfitReportTable = observer(function UserProfitReportTable({
  store,
}: UserProfitReportTableProps) {
  const { columns, defaultHiddenColumnIds } = useMemo(() => {
    if (store.groupBy) {
      return {
        columns: [] as BodyColumnDef<
          UserProfitReportRow,
          UtilizationReportOrderByColumn
        >[],
        defaultHiddenColumnIds: [] as string[],
      };
    }

    const isDateInPast = differenceInMonths(store.dateRange.start, now()) < 0;
    const defaultHiddenColumnIds = ["position", "left-to-allocate", "overtime"];

    let sourceCol: BodyColumnDef<
      UserProfitReportRow,
      UtilizationReportOrderByColumn
    >;
    const billableCol: BodyColumnDef<
      UserProfitReportRow,
      UtilizationReportOrderByColumn
    > = {
      id: "billable",
    };

    switch (store.dataSource) {
      case UtilizationReportDataSource.TimeTracking:
        sourceCol = {
          id: "tracked",
          meta: { label: t`Tracked` },
          header: () => t`Tracked`,
          orderBy: UtilizationReportOrderByColumn.TrackedTime,
          cell: ({ row }) => (
            <span>
              {formatSecondsToHours(row.original.report.tracked_time ?? 0, 1)}
            </span>
          ),
        };
        billableCol.meta = { label: t`Tracked (Billable)` };
        billableCol.header = () => t`Tracked (Billable)`;
        billableCol.orderBy =
          UtilizationReportOrderByColumn.TrackedTimeBillable;
        billableCol.cell = ({ row }) => (
          <span>
            {formatSecondsToHours(
              row.original.report.tracked_time_billable ?? 0,
              1,
            )}
          </span>
        );
        break;
      case UtilizationReportDataSource.Planning:
        sourceCol = {
          id: "allocated",
          meta: { label: t`Allocated` },
          header: () => t`Allocated`,
          orderBy: UtilizationReportOrderByColumn.AllocatedTime,
          cell: ({ row }) => (
            <span>
              {formatSecondsToHours(row.original.report.allocated_time ?? 0, 1)}
            </span>
          ),
        };
        billableCol.meta = { label: t`Allocated (Billable)` };
        billableCol.header = () => t`Allocated (Billable)`;
        billableCol.orderBy =
          UtilizationReportOrderByColumn.AllocatedTimeBillable;
        billableCol.cell = ({ row }) => (
          <span>
            {formatSecondsToHours(
              row.original.report.allocated_time_billable ?? 0,
              1,
            )}
          </span>
        );
        break;
      case UtilizationReportDataSource.TaskPositions:
        sourceCol = {
          id: "task-budgeted",
          meta: { label: t`Task budgeted` },
          header: () => t`Task budgeted`,
          orderBy: UtilizationReportOrderByColumn.BudgetedTime,
          cell: ({ row }) => (
            <span>
              {formatSecondsToHours(row.original.report.budgeted_time ?? 0, 1)}
            </span>
          ),
        };
        billableCol.meta = { label: t`Task budgeted (Billable)` };
        billableCol.header = () => (
          <Text
            wordBreak="break-word"
            whiteSpace="break-spaces"
            minW="120px"
            fontWeight="inherit"
            fontSize="inherit"
            textAlign="start"
          >
            <Trans>Task budgeted (Billable)</Trans>
          </Text>
        );
        billableCol.orderBy =
          UtilizationReportOrderByColumn.BudgetedTimeBillable;
        billableCol.cell = ({ row }) => (
          <span>
            {formatSecondsToHours(
              row.original.report.budgeted_time_billable ?? 0,
              1,
            )}
          </span>
        );
        break;
      case UtilizationReportDataSource.Combined:
        sourceCol = {
          id: "tracked+allocated",
          meta: { label: t`Tracked + Allocated` },
          header: () => t`Tracked + Allocated`,
          orderBy: UtilizationReportOrderByColumn.CombinedTiMe,
          cell: ({ row }) => (
            <TrackedAndAllocatedTooltip
              row={row.original}
              date={store.dateRange.start}
            >
              <span>
                {formatSecondsToHours(
                  row.original.report.combined_time ?? 0,
                  1,
                )}
              </span>
            </TrackedAndAllocatedTooltip>
          ),
        };
        billableCol.meta = { label: t`Tracked + Allocated (Billable)` };
        billableCol.header = () => (
          <Text
            wordBreak="break-word"
            whiteSpace="break-spaces"
            minW="120px"
            fontWeight="inherit"
            fontSize="inherit"
            textAlign="start"
          >
            <Trans>Tracked + Allocated (Billable)</Trans>
          </Text>
        );
        billableCol.orderBy =
          UtilizationReportOrderByColumn.CombinedTimeBillable;
        billableCol.cell = ({ row }) => (
          <span>
            {formatSecondsToHours(
              row.original.report.combined_time_billable ?? 0,
              1,
            )}
          </span>
        );
        break;
      default:
        sourceCol = {
          id: "tracked",
          meta: { label: t`Tracked` },
          header: () => t`Tracked`,
          orderBy: UtilizationReportOrderByColumn.TrackedTime,
          cell: ({ row }) => (
            <span>
              {formatSecondsToHours(row.original.report.tracked_time ?? 0, 1)}
            </span>
          ),
        };
        billableCol.meta = { label: t`Billable` };
        billableCol.header = () => t`Billable`;
        billableCol.orderBy =
          UtilizationReportOrderByColumn.TrackedTimeBillable;
        billableCol.cell = ({ row }) => (
          <span>
            {formatSecondsToHours(
              row.original.report.tracked_time_billable ?? 0,
              1,
            )}
          </span>
        );
        break;
    }

    const hiddenForTaskPositions =
      store.dataSource === UtilizationReportDataSource.TaskPositions;

    const columns: BodyColumnDef<
      UserProfitReportRow,
      UtilizationReportOrderByColumn
    >[] = [
      {
        id: "name",
        meta: { label: t`Name` },
        header: () => t`Name`,
        enableHiding: false,
        cell: ({ row }) =>
          userCell(row.original.user, () =>
            store.userModalStore.modalState.onOpen({
              id: row.original.user.id,
              dataSource: store.dataSource,
              dateRange: store.dateRange,
            }),
          ),
      },
      {
        id: "team",
        meta: { label: t`Team` },
        header: () => t`Team`,
        orderBy: UtilizationReportOrderByColumn.TeamName,
        cell: ({ row }) => <span>{row.original.user.team?.name}</span>,
      },
      {
        id: "position",
        meta: { label: t`Position` },
        header: () => t`Position`,
        orderBy: UtilizationReportOrderByColumn.WorkTypeTitle,
        cell: ({ row }) => (
          <span>{row.original.user.profile?.defaultWorktype.title}</span>
        ),
      },
      {
        id: "available-capacity",
        meta: { label: t`Available Capacity` },
        header: () => t`Available Capacity`,
        orderBy: UtilizationReportOrderByColumn.AvailableTime,
        cell: ({ row }) => (
          <AvailableCapacityTooltip row={row.original}>
            <span>
              {formatSecondsToHours(row.original.report.available_time, 1)}
            </span>
          </AvailableCapacityTooltip>
        ),
      },
      sourceCol,
      billableCol,
      {
        id: "tracked-time-billable",
        meta: { label: t`Tracked time` },
        header: () => t`Tracked time`,
        cell: ({ row }) =>
          formatSecondsToHours(
            row.original.report.tracked_time_billable ?? 0,
            1,
          ),
      },
      {
        id: "allocated-time-billable",
        meta: { label: t`Allocated time` },
        header: () => t`Allocated time`,
        cell: ({ row }) =>
          formatSecondsToHours(
            row.original.report.allocated_time_billable ?? 0,
            1,
          ),
      },
      {
        id: "left-to-allocate",
        meta: { label: isDateInPast ? t`Unused hours` : t`Left to allocate` },
        header: () => (isDateInPast ? t`Unused hours` : t`Left to allocate`),
        cell: ({ row }) =>
          row.original.report.remaining_time > 0 ? (
            <span>
              {formatSecondsToHours(row.original.report.remaining_time, 1)}
            </span>
          ) : null,
      },
      {
        id: "overtime",
        meta: { label: t`Overtime` },
        header: () => t`Overtime`,
        cell: ({ row }) =>
          row.original.report.overtime > 0 ? (
            <span>{formatSecondsToHours(row.original.report.overtime, 1)}</span>
          ) : null,
      },
      {
        id: "revenue-estimate",
        meta: { label: t`Revenue estimate` },
        header: () => t`Revenue estimate`,
        cell: ({ row }) =>
          currency.formatByWorkspace(
            store.dataSource === UtilizationReportDataSource.Combined
              ? (row.original.report.combined_amount_billable ?? 0)
              : (row.original.report.budgeted_amount_billable ?? 0),
          ),
      },
      {
        id: "internal-cost",
        meta: { label: t`Internal cost` },
        header: () => t`Internal cost`,
        cell: ({ row }) =>
          currency.formatByWorkspace(row.original.report.internal_cost ?? 0),
      },
      {
        id: "combined-profit-amount",
        meta: { label: t`User profit` },
        header: () => t`User profit`,
        cell: ({ row }) => {
          const amount =
            store.dataSource === UtilizationReportDataSource.Combined
              ? (row.original.report.combined_profit_amount ?? 0)
              : (row.original.report.budgeted_profit_amount ?? 0);

          return (
            <Text color={amount < 0 ? "red.500" : undefined}>
              {currency.formatByWorkspace(amount)}
            </Text>
          );
        },
      },
      {
        id: "utilization",
        meta: { label: t`Utilization` },
        header: () => t`Utilization`,
        orderBy: UtilizationReportOrderByColumn.UtilizationPercentage,
        cell: ({ row }) => (
          <HStack>
            <RangeProgressBar
              w="96px"
              progress={row.original.report.utilization_percentage ?? 0}
              exceededThreshold={{
                below: row.original.user.profile?.utilization_range_from ?? 0,
                above: row.original.user.profile?.utilization_range_to ?? 0,
              }}
            />
            <Text minW="10" textAlign="end">
              {row.original.report.utilization_percentage ?? 0}%
            </Text>
          </HStack>
        ),
      },
      {
        id: "billable-utilization",
        meta: { label: t`Billable utilization` },
        header: () => t`Billable utilization`,
        orderBy: UtilizationReportOrderByColumn.UtilizationPercentageBillable,
        cell: ({ row }) => (
          <HStack>
            <RangeProgressBar
              w="96px"
              progress={
                row.original.report.utilization_percentage_billable ?? 0
              }
              exceededThreshold={{
                below: row.original.user.profile?.utilization_range_from ?? 0,
                above: row.original.user.profile?.utilization_range_to ?? 0,
              }}
            />
            <Text minW="10" textAlign="end">
              {row.original.report.utilization_percentage_billable ?? 0}%
            </Text>
          </HStack>
        ),
      },
      {
        id: ACTIONS_COLUMN_ID,
        meta: { label: t`Actions` },
        header: () => t`Actions`,
        cell: ({ row }) => (
          <Button
            fontWeight="medium"
            onClick={() => {
              store.userModalStore.modalState.onOpen({
                id: row.original.user.id,
                dataSource: store.dataSource,
                dateRange: store.dateRange,
              });
            }}
            role="link"
            size="xs"
            variant="link"
          >
            <Trans>Detail</Trans>
          </Button>
        ),
      },
    ];

    return {
      columns: columns.filter((column) => {
        if (hiddenForTaskPositions) {
          return ![
            "allocated-time-billable",
            "left-to-allocate",
            "overtime",
          ].includes(column.id as string);
        }

        return true;
      }),
      defaultHiddenColumnIds: defaultHiddenColumnIds.filter(
        (id) =>
          !hiddenForTaskPositions ||
          (id !== "left-to-allocate" && id !== "overtime"),
      ),
    };
  }, [
    store.dataSource,
    store.dateRange,
    store.groupBy,
    store.userModalStore.modalState,
  ]);

  return (
    <Box pos="relative">
      <LoadingOverlay isLoading={store.isReportLoading} position="absolute">
        <DataTable.Body<UserProfitReportRow, UtilizationReportOrderByColumn>
          tableKey={store.moduleKey}
          data={store.tableData}
          columns={columns}
          defaultHiddenColumnIds={defaultHiddenColumnIds}
          orderedBy={store.activeOrderBy}
          onOrderByChange={store.handleChangeOrderBy}
        />
      </LoadingOverlay>
    </Box>
  );
});
